import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import env from "app/utils/env";
import type { HotelAccommodationDescription } from "app/features/hotel/hotelDescription/types";
import { mapHotelOnlyAccommodation } from "app/features/hotel/hotelDescription/utils/mapHotelOnlyAccommodation";
import type { Tunnel } from "app/types/tunnel";

const fetchHotelAccommodationDescription = async (
	accommodationId: string,
	tunnel: Tunnel,
	partnerCode: string
): Promise<HotelAccommodationDescription> => {
	const isHotelOnly = tunnel === "hotelonly";
	const path = isHotelOnly
		? `/hotelonly/accommodations/${accommodationId}`
		: `/${tunnel}/accommodationDescription/${accommodationId}`;

	// never send Booking-Flow-Id here: AWS security rejects it on this cached endpoint (FBR-5054)
	const response = await axios.get(`${env("CACHED_API_URL")}${path}`, {
		params: { partnerCode },
	});

	return isHotelOnly
		? mapHotelOnlyAccommodation(response.data)
		: (response.data as HotelAccommodationDescription);
};

export const getHotelAccommodationDescriptionQuery = (
	accommodationId: string | undefined,
	tunnel: Tunnel,
	partnerCode: string
) => ({
	queryKey: ["hotelAccommodationDescription", accommodationId, tunnel, partnerCode],
	queryFn: () =>
		fetchHotelAccommodationDescription(accommodationId as string, tunnel, partnerCode),
	enabled: Boolean(accommodationId) && Boolean(partnerCode),
	staleTime: 1000 * 60 * 5,
});

export const useHotelAccommodationDescription = (
	accommodationId: string | undefined,
	tunnel: Tunnel,
	partnerCode: string,
	{ enabled = true }: { enabled?: boolean } = {}
) => {
	const query = getHotelAccommodationDescriptionQuery(accommodationId, tunnel, partnerCode);

	return useQuery({ ...query, enabled: query.enabled && enabled });
};
